import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "./components/SEO";
import { PulseSection } from "./pages/landing/PulseSection";
import { SheetWorld } from "./components/sheet/SheetWorld";
import { SheetFooter, SheetNav } from "./components/sheet/Chrome";
import { Thread } from "./components/sheet/Thread";
import { Hero } from "./pages/landing/sheet/Hero";
import { BraneSection, KnotSection, LoopSection, Rooms, StringSection } from "./pages/landing/sheet/Rooms";
import { Close } from "./pages/landing/sheet/Close";
// After the shared world (SheetWorld imports components/sheet/sheet.css): the landing's own sections.
import "./pages/landing/sheet/landing.css";

/*
  Landing page: the One Sheet world, where every connection is cut from the
  same sheet of paper (.impeccable/surfaces/src-pages-landing.md). This file is
  composition only; the world, its token layer and its figures live in
  components/sheet/ (shared with every public page), and the landing's own
  sections in pages/landing/sheet/.

  The story runs top to bottom: the line at poster scale with a strip woven
  through it, over the Loops demo (art first, then the evidence); the three
  rooms side by side; one room each on its own colour of paper (String, then
  the Brane, its other way in, on the bare sheet; Knot; Loop); the Pulse; and
  the closing block.
*/
/* Skip-link target. `y-main` is the app shell's equivalent (pages/y/layout.tsx). */
const MAIN_ID = "landing-main";

export default function App() {
  const { t } = useTranslation();
  const { hash } = useLocation();

  /* Arriving with a room's hash (the nav's room links from About or FAQ), go
     to it once the page has laid out: the browser's own jump runs before the
     sections exist. */
  useEffect(() => {
    if (!hash) return;
    const frame = requestAnimationFrame(() => document.getElementById(hash.slice(1))?.scrollIntoView());
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  /*
    Title and description read through i18n so the default locale gets Mongolian
    metadata rather than the English the tags were hardcoded to. The keys do not
    exist in en.json/mn.json yet — landing-redesign issue 02 owns the copy and is
    gated on human-approved Mongolian — so the English sentences that shipped
    here stand as the default until it lands. The description carries no
    quantitative claim (PRODUCT.md, Claims Policy).
  */
  return (
    <SheetWorld>
      <SEO
        title={t("seo.landing.title", {
          defaultValue: "aSTRING — talk freely, stay verified",
        })}
        description={t("seo.landing.description", {
          defaultValue:
            "A verified space for students and professionals. Loops are open topic rooms at your university, Knots are communities you join, Strings are one-to-one.",
        })}
      />

      {/* First tab stop on the page. Copy is the app shell's existing key. */}
      <a href={`#${MAIN_ID}`} className="skip-link">
        {t("y.layout.skipToContent")}
      </a>

      <SheetNav />
      {/*
        The footer stays outside <main>: it is its own landmark.
      */}
      <main id={MAIN_ID} className="sh-threaded">
        <Hero />
        <Rooms />
        <StringSection />
        <BraneSection />
        <KnotSection />
        <LoopSection />
        {/*
          The Pulse: the only figures on the page, rendered from
          GET /api/v1/pulse at view time with their as-of time (PRODUCT.md,
          Claims Policy amendment of 2026-10-08). It renders nothing when the
          endpoint is down, and the page reads complete without it.
        */}
        <div className="sh-pulse">
          <PulseSection />
        </div>
        <Close />
        {/* One strip from the hero's headline to the closing one, drawn down the page as it is read. */}
        <Thread />
      </main>
      <SheetFooter />
    </SheetWorld>
  );
}
